import type { UseFetchFromPublicWabunkaApiArgs } from '@/composables/useFetchFromPublicWabunkaApi';
import { useFetchKey, useRuntimeConfigKey, type UseFetch, type UseRuntimeConfig } from '@/injectionKeys';
import { isWabunkaError, WabunkaAPIError } from '@/models/error';
import { buildFetchKey } from '@/wabunka-src/common/utils/buildFetchKey';

/**
 * ヘッダーの組み立てをおこなう
 * @returns ヘッダー用オブジェクト
 */
const buildHeader = () => {
  return {
    // eslint-disable-next-line
    'Content-Type': 'application/json',
    Accept: 'application/json',
    // eslint-disable-next-line
    'X-Wabunka-Language': 'en',
  };
};

/**
 * Public Wabunka API からデータを取得する useFetch ラッパー Composable
 * ページ層では直接使用せず、ドメイン知識を有した Composable を介して利用してください
 *
 * @param UseFetchFromPublicWabunkaApiArgs
 * @return data と status
 */
export const useAsyncFetchFromPublicApi = async <T>({ path, query, method, body, options, converter, fetchKey }: UseFetchFromPublicWabunkaApiArgs<T>) => {
  const useRuntimeConfig = inject(useRuntimeConfigKey) as UseRuntimeConfig;
  const runtimeConfig = useRuntimeConfig();
  const useFetch = inject(useFetchKey) as UseFetch;

  // Nuxt 4 では useFetch が同じ key のときにキャッシュを共有するため、
  // リクエストを区別する入力をすべて key に含める。とくに body を落とすと、同じ path への
  // 別内容の POST が 1 回目の応答をそのまま返す。
  // fetchKey が指定された場合はそれを最優先（呼び出し側が完全に key を制御したい特殊ケース）。
  const { customKey, ...restOptions } = options ?? {};
  const key = fetchKey ?? buildFetchKey(path.value, query?.value, customKey, { method, body: body?.value });

  const { data, error, status, refresh } = await useFetch(path, {
    key,
    ...restOptions,
    query,
    // method / body を落とすと、POST を渡した呼び出しが無言で GET になり本文も飛ばない
    ...(method !== undefined ? { method } : {}),
    ...(body !== undefined ? { body } : {}),
    baseURL: runtimeConfig.public.WABUNKA_PUBLIC_API_BASE_URL as string,
    onRequest({ options }) {
      options.headers = new Headers(buildHeader());
    },
  });

  if (error.value != null) {
    const data = error.value.data;
    if (isWabunkaError(data)) {
      throw new WabunkaAPIError(data);
    }
    throw error.value;
  }
  const convertedData = computed<T | null>(() => {
    if (data.value == null) {
      return null;
    }
    return converter(data.value);
  });

  return { data: convertedData, status, refresh };
};
